import React, { useEffect, useRef, useState } from "react";

import { LOGIN_URL, SIGNUP_URL, CONTACT, BROCHURES, LINKEDIN_URL } from "./siteConfig";
import { PRODUCTS, DMS_PILLARS } from "./siteContent";

/*
  Header and footer are shared by the marketing home page and the standalone
  legal/support pages, so they live here instead of inside SiteHome. Section
  links use a leading "/" (e.g. "/#compare") rather than a bare hash, because
  a bare "#compare" clicked from /privacy would just scroll a page that has
  no such element instead of going home first.
*/
const NAV_LINKS = [
  { href: "/#problem", label: "Why DecisionFlow" },
  { href: "/#compare", label: "How we compare" },
];

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45z" />
    </svg>
  );
}

export function Logo() {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <span className="site-logo-fallback">D</span>;
  }
  return (
    <img
      className="site-logo"
      src="/site/logo.png"
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

function ProductsMenu({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = () => {
    setOpen(false);
    onNavigate();
  };

  return (
    <div className={`site-has-menu ${open ? "is-open" : ""}`} ref={ref}>
      <button
        type="button"
        className="site-nav-trigger"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        Products
        <span className="site-caret" aria-hidden="true" />
        <span className="site-nav-count">2</span>
      </button>

      <div className="site-menu">
        <p className="site-menu-label">Two versions, one system</p>
        {PRODUCTS.map((p, i) => (
          <a className="site-menu-item" href={`/#${p.id}`} key={p.id} onClick={go}>
            <span className="site-menu-n">{String(i + 1).padStart(2, "0")}</span>
            <span>
              <strong>{p.name}</strong>
              <em>{p.who}</em>
            </span>
          </a>
        )).flatMap((item, i) =>
          // The project version is two products in one; name both halves.
          PRODUCTS[i].id === "dms"
            ? [
                item,
                <div className="site-menu-sub" key="dms-sub">
                  {DMS_PILLARS.map((pl) => (
                    <a href={`/#${pl.id}`} key={pl.id} onClick={go}>
                      {pl.title}
                    </a>
                  ))}
                </div>,
              ]
            : [item],
        )}
      </div>
    </div>
  );
}

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="site-header">
      <div className="site-container site-header-inner">
        <a className="site-brand" href="/">
          <Logo />
          <span>DecisionFlow</span>
        </a>

        <nav className={`site-nav ${menuOpen ? "is-open" : ""}`}>
          <ProductsMenu onNavigate={closeMenu} />
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={closeMenu}>
              {l.label}
            </a>
          ))}
          <a className="site-nav-login" href={LOGIN_URL()} onClick={closeMenu}>
            Log in
          </a>
          <a className="site-btn site-btn-primary" href={SIGNUP_URL()} onClick={closeMenu}>
            Start free trial
          </a>
        </nav>

        <button
          type="button"
          className="site-menu-toggle"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className={menuOpen ? "is-open" : ""} />
        </button>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-container site-footer-inner">
        <div>
          <a className="site-brand" href="/">
            <Logo />
            <span>DecisionFlow</span>
          </a>
          <p className="site-footer-note">
            Project tracking and production tracking, for teams and factories
            that deliver to customers.
          </p>
        </div>

        <div className="site-footer-links">
          {PRODUCTS.map((p) => (
            <a key={p.id} href={`/#${p.id}`}>
              {p.name}
            </a>
          ))}
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href}>
              {l.label}
            </a>
          ))}
          <a href={LOGIN_URL()}>Log in</a>
        </div>

        <div className="site-footer-links">
          <span className="site-footer-head">Brochures</span>
          {Object.entries(BROCHURES).map(([id, b]) => (
            <a key={id} href={b.href} download>
              {b.name}
            </a>
          ))}
          <span className="site-footer-head">Legal &amp; support</span>
          <a href="/privacy">Privacy policy</a>
          <a href="/terms">Terms of service</a>
          <a href="/support">Support</a>
          <a href="/ai-assistants">AI assistants</a>
        </div>

        <div className="site-footer-contact">
          <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
          <a href={`tel:${CONTACT.phoneHref}`}>{CONTACT.phone}</a>
          <a href={LOGIN_URL()}>{CONTACT.app}</a>
        </div>

        <div className="site-footer-links">
          <span className="site-footer-head">Links</span>
          <a
            className="site-footer-social"
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="DecisionFlow on LinkedIn"
          >
            <LinkedInIcon />
            LinkedIn
          </a>
        </div>
      </div>

      <div className="site-container site-footer-base">
        <span>© {new Date().getFullYear()} DecisionFlow. All rights reserved.</span>
      </div>
    </footer>
  );
}
